import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Textarea,
} from '@bookaburra/ui';
import { AlertTriangle, ArrowRight, BellRing, CheckCircle2, XCircle } from 'lucide-react';
import { AppHeader } from '../components/AppHeader';
import { useDocumentTitle } from '../lib/use-document-title';
import { authClient } from '../auth/client';

interface BookingSummary {
  bookingId: string;
  serviceId: string;
  serviceName?: string;
  rangeStart: number;
  rangeEnd: number;
  status:
    | 'confirmed'
    | 'cancelled'
    | 'rescheduled'
    | 'pending_approval'
    | 'rejected'
    | 'no_show';
  onSite: boolean;
  approvalResponse: string | null;
  tenantName?: string;
  tenantTimezone?: string;
  allowedActions: ('cancel' | 'confirm' | 'reschedule' | 'approve' | 'reject')[];
  client: { name: string; email: string | null; phone: string | null } | null;
}

export const Route = createFileRoute('/m/$token')({
  component: ManagePage,
});

async function fetchSummary(token: string): Promise<BookingSummary> {
  const r = await fetch(`/api/m/${encodeURIComponent(token)}`);
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `error ${r.status}`);
  }
  return r.json();
}

async function cancelBooking(token: string): Promise<void> {
  const r = await fetch(`/api/m/${encodeURIComponent(token)}/cancel`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      // Set Origin explicitly so the worker's same-origin check is happy
      // even when running behind some proxies / form submits.
    },
  });
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `error ${r.status}`);
  }
}

async function claimBooking(token: string): Promise<{ claimed: boolean }> {
  const r = await fetch(`/api/m/${encodeURIComponent(token)}/claim`, {
    method: 'POST',
  });
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `error ${r.status}`);
  }
  return r.json();
}

async function decideApproval(
  token: string,
  action: 'approve' | 'reject',
  responseMessage: string,
): Promise<void> {
  const r = await fetch(`/api/m/${encodeURIComponent(token)}/${action}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ responseMessage }),
  });
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `error ${r.status}`);
  }
}

function ManagePage() {
  const { token } = Route.useParams();
  const summary = useQuery({
    queryKey: ['manage', token],
    queryFn: () => fetchSummary(token),
    retry: false,
  });
  useDocumentTitle(
    summary.data?.tenantName
      ? `Manage booking · ${summary.data.tenantName}`
      : 'Manage booking',
  );

  const { data: session, isPending: sessionPending } = authClient.useSession();
  const signedIn = Boolean(session?.user);

  // Customer-facing token (manage/cancel/reschedule/confirm) → claim
  // makes sense. Operator-facing tokens (approve/reject) embed staff
  // context, not customer ownership, so skip the CTA + auto-claim.
  const isCustomerToken = summary.data
    ? !summary.data.allowedActions.some(
        (a) => a === 'approve' || a === 'reject',
      )
    : false;

  const cancelMut = useMutation({
    mutationFn: () => cancelBooking(token),
    onSuccess: () => summary.refetch(),
  });

  // Fire-and-forget claim once the user is signed in and the booking
  // summary has loaded. Idempotent server-side — re-claims are a no-op.
  // We only attempt once per page load to avoid hammering the API.
  const [claimAttempted, setClaimAttempted] = useState(false);
  const [claimResult, setClaimResult] = useState<'idle' | 'claimed' | 'noop'>(
    'idle',
  );
  useEffect(() => {
    if (claimAttempted) return;
    if (!signedIn || !summary.data || !isCustomerToken) return;
    setClaimAttempted(true);
    claimBooking(token)
      .then((r) => setClaimResult(r.claimed ? 'claimed' : 'noop'))
      .catch(() => setClaimResult('noop'));
  }, [signedIn, summary.data, isCustomerToken, claimAttempted, token]);

  const [responseMessage, setResponseMessage] = useState('');
  const decideMut = useMutation({
    mutationFn: (action: 'approve' | 'reject') =>
      decideApproval(token, action, responseMessage),
    onSuccess: () => summary.refetch(),
  });

  return (
    <div className="min-h-dvh bg-background">
      <AppHeader />
      <main className="mx-auto flex max-w-md flex-col px-6 py-12 sm:py-16">
        <header className="mb-6 text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary">
            Manage your booking
          </p>
          {summary.data?.tenantName ? (
            <h1 className="mt-2 text-xl font-semibold tracking-tight">
              {summary.data.tenantName}
            </h1>
          ) : null}
        </header>

      {summary.data && isCustomerToken && !sessionPending && !signedIn ? (
        <div className="mb-6 rounded-2xl border border-brand-sage/30 bg-brand-cream p-5 shadow-brand">
          <div className="flex items-start gap-3">
            <BellRing className="mt-0.5 h-5 w-5 shrink-0 text-brand-moss" />
            <div className="flex-1">
              <h2 className="text-base font-semibold text-foreground">
                Never miss a booking reminder
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Create a free account to keep this booking in one place,
                receive reliable reminders, and manage future appointments
                without digging through emails.
              </p>
              <Link
                to="/signup"
                search={{ redirect: `/m/${token}` }}
                className="mt-3 inline-flex items-center gap-1 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:bg-primary/90"
              >
                Create account <ArrowRight className="h-4 w-4" />
              </Link>
              <p className="mt-2 text-xs text-muted-foreground">
                Already have one?{' '}
                <Link
                  to="/signin"
                  search={{ redirect: `/m/${token}` }}
                  className="font-medium text-primary underline-offset-4 hover:underline"
                >
                  Sign in
                </Link>
                .
              </p>
            </div>
          </div>
        </div>
      ) : null}

      {claimResult === 'claimed' ? (
        <Alert variant="success" className="mb-4">
          <CheckCircle2 className="h-4 w-4" />
          <AlertTitle>Booking linked to your account</AlertTitle>
          <AlertDescription>
            This appointment now appears in your bookings list whenever you
            sign in.
          </AlertDescription>
        </Alert>
      ) : null}

      {summary.isLoading ? <div className="h-40 animate-pulse rounded-2xl bg-secondary" /> : null}

      {summary.error ? (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertTitle>Link expired or invalid</AlertTitle>
          <AlertDescription>
            This management link is no longer valid. If you need to change a
            booking, contact the business directly.
          </AlertDescription>
        </Alert>
      ) : null}

      {summary.data ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between gap-2">
              <span>Your appointment</span>
              <StatusBadge status={summary.data.status} />
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                When
              </p>
              <p className="mt-1 text-base">
                {`${new Date(summary.data.rangeStart).toLocaleDateString(
                  'en-AU',
                  {
                    dateStyle: 'full',
                    timeZone: summary.data.tenantTimezone,
                  },
                )} at ${new Date(summary.data.rangeStart).toLocaleTimeString(
                  'en-AU',
                  {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false,
                    timeZone: summary.data.tenantTimezone,
                  },
                )}`}
              </p>
              <p className="text-sm text-muted-foreground">
                Duration:{' '}
                {Math.round(
                  (summary.data.rangeEnd - summary.data.rangeStart) / 60000,
                )}{' '}
                min
              </p>
            </div>

            {summary.data.status === 'cancelled' ? (
              <Alert variant="success">
                <CheckCircle2 className="h-4 w-4" />
                <AlertTitle>Booking cancelled</AlertTitle>
                <AlertDescription>
                  Your appointment has been cancelled. The team has been notified.
                </AlertDescription>
              </Alert>
            ) : null}

            {summary.data.status === 'pending_approval' &&
            !summary.data.allowedActions.includes('approve') ? (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Pending approval</AlertTitle>
                <AlertDescription>
                  {summary.data.tenantName ?? 'The business'} is reviewing your
                  booking request. You'll get an email as soon as it's
                  confirmed.
                </AlertDescription>
              </Alert>
            ) : null}

            {summary.data.status === 'rejected' ? (
              <Alert variant="destructive">
                <XCircle className="h-4 w-4" />
                <AlertTitle>Booking declined</AlertTitle>
                <AlertDescription>
                  {summary.data.tenantName ?? 'The business'} wasn't able to
                  confirm this booking.
                  {summary.data.approvalResponse ? (
                    <span className="mt-2 block whitespace-pre-wrap">
                      {summary.data.approvalResponse}
                    </span>
                  ) : null}
                </AlertDescription>
              </Alert>
            ) : null}

            {summary.data.status === 'pending_approval' &&
            summary.data.allowedActions.includes('approve') &&
            summary.data.client ? (
              <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 space-y-3">
                <div>
                  <p className="text-sm font-medium">Approval required</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Customer:{' '}
                    <span className="font-medium">
                      {summary.data.client.name}
                    </span>
                    {summary.data.client.email
                      ? ` · ${summary.data.client.email}`
                      : ''}
                    {summary.data.client.phone
                      ? ` · ${summary.data.client.phone}`
                      : ''}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                    Message to customer (optional)
                  </label>
                  <Textarea
                    value={responseMessage}
                    onChange={(e) => setResponseMessage(e.target.value)}
                    rows={3}
                    maxLength={1000}
                    placeholder="Add a short note — sent in the email back to them."
                    className="mt-1"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => decideMut.mutate('approve')}
                    loading={decideMut.isPending && decideMut.variables === 'approve'}
                    disabled={decideMut.isPending}
                  >
                    Approve booking
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => decideMut.mutate('reject')}
                    loading={decideMut.isPending && decideMut.variables === 'reject'}
                    disabled={decideMut.isPending}
                  >
                    Reject
                  </Button>
                </div>
                {decideMut.error ? (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>Couldn't process</AlertTitle>
                    <AlertDescription>
                      {(decideMut.error as Error).message}
                    </AlertDescription>
                  </Alert>
                ) : null}
              </div>
            ) : null}

            {summary.data.status === 'confirmed' &&
            summary.data.allowedActions.includes('cancel') ? (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4">
                <p className="text-sm font-medium">Need to cancel?</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  This will free up the slot for someone else. You can&rsquo;t
                  undo it from this link.
                </p>
                <Button
                  variant="destructive"
                  size="sm"
                  className="mt-3"
                  loading={cancelMut.isPending}
                  onClick={() => cancelMut.mutate()}
                  disabled={summary.data.status !== 'confirmed'}
                >
                  Cancel booking
                </Button>
                {cancelMut.error ? (
                  <Alert variant="destructive" className="mt-3">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>Couldn&rsquo;t cancel</AlertTitle>
                    <AlertDescription>{(cancelMut.error as Error).message}</AlertDescription>
                  </Alert>
                ) : null}
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      <p className="mt-6 text-center text-xs text-muted-foreground">
        This management link is unique to your booking and expires after your
        appointment.
      </p>
      </main>
    </div>
  );
}

function StatusBadge({
  status,
}: {
  status: BookingSummary['status'];
}) {
  if (status === 'cancelled') return <Badge variant="destructive">Cancelled</Badge>;
  if (status === 'rejected') return <Badge variant="destructive">Declined</Badge>;
  if (status === 'no_show') return <Badge variant="destructive">No-show</Badge>;
  if (status === 'rescheduled') return <Badge variant="warning">Rescheduled</Badge>;
  if (status === 'pending_approval')
    return <Badge variant="warning">Pending approval</Badge>;
  return <Badge variant="success">Confirmed</Badge>;
}
